/**
 * Shared constants used across the application
 */
// ============================================================================
// User Constants
// ============================================================================
export const USER_TYPES = {
    EMPLOYEE: 'employee',
    CONTRACTOR: 'contractor',
};
export const USER_STATUSES = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    PENDING: 'pending',
};
// ============================================================================
// Task Constants
// ============================================================================
export const TASK_STATUSES = {
    NOT_STARTED: 'not_started',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    BLOCKED: 'blocked',
};
export const TASK_PRIORITIES = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical',
};
// ============================================================================
// Stage Constants
// ============================================================================
export const STAGE_STATUSES = {
    PENDING: 'pending',
    ACTIVE: 'active',
    COMPLETED: 'completed',
};
// ============================================================================
// Notification Constants
// ============================================================================
export const NOTIFICATION_TYPES = {
    TASK_ASSIGNED: 'task_assigned',
    TASK_COMPLETED: 'task_completed',
    TASK_OVERDUE: 'task_overdue',
    STAGE_COMPLETED: 'stage_completed',
    DOCUMENT_UPLOADED: 'document_uploaded',
    ACTION_REQUIRED: 'action_required',
};
export const NOTIFICATION_STATUSES = {
    UNREAD: 'unread',
    READ: 'read',
    ARCHIVED: 'archived',
};
export const NOTIFICATION_CHANNELS = {
    EMAIL: 'email',
    IN_APP: 'in_app',
    BOTH: 'both',
};
// ============================================================================
// Document Constants
// ============================================================================
export const DOCUMENT_TYPES = {
    FORM: 'form',
    POLICY: 'policy',
    CONTRACT: 'contract',
    IDENTIFICATION: 'identification',
    CERTIFICATE: 'certificate',
    OTHER: 'other',
};
export const DOCUMENT_STATUSES = {
    PENDING: 'pending',
    UPLOADED: 'uploaded',
    APPROVED: 'approved',
    REJECTED: 'rejected',
};
// ============================================================================
// Auth Constants
// ============================================================================
export const USER_ROLES = {
    ADMIN: 'admin',
    MANAGER: 'manager',
    EMPLOYEE: 'employee',
    HR: 'hr',
    EXTERNAL: 'external',
};
export const ORG_ROLES = ['admin', 'manager', 'employee', 'hr'];
export const AUTH_PROVIDERS = {
    LOCAL: 'local',
    GOOGLE: 'google',
};
export const INVITATION_STATUSES = {
    PENDING: 'pending',
    ACCEPTED: 'accepted',
    EXPIRED: 'expired',
    REVOKED: 'revoked',
};
export const TOKEN_STORAGE_KEYS = {
    ACCESS_TOKEN: 'ob_access_token',
    REFRESH_TOKEN: 'ob_refresh_token',
};
// ============================================================================
// Hiring Pipeline Constants
// ============================================================================
export const CANDIDATE_STAGES = {
    APPLICATION: 'application',
    INTERVIEW: 'interview',
    OFFER: 'offer',
    AGREEMENT_SIGNING: 'agreement_signing',
    BACKGROUND_CHECK: 'background_check',
    ONBOARDING_DOCS: 'onboarding_docs',
    COMPLETE: 'complete',
    REJECTED: 'rejected',
    WITHDRAWN: 'withdrawn',
};
export const HIRE_TYPES = {
    REGULAR_HIRE: 'regular_hire',
    DAS_ONBOARDING: 'das_onboarding',
    EXECUTIVE: 'executive',
};
export const HIRE_TYPE_LABELS = {
    [HIRE_TYPES.REGULAR_HIRE]: 'Regular Hire',
    [HIRE_TYPES.DAS_ONBOARDING]: 'DAS Onboarding',
    [HIRE_TYPES.EXECUTIVE]: 'Executive',
};
export const HIRE_TYPE_STARTING_STAGE = {
    [HIRE_TYPES.REGULAR_HIRE]: CANDIDATE_STAGES.APPLICATION,
    [HIRE_TYPES.DAS_ONBOARDING]: CANDIDATE_STAGES.ONBOARDING_DOCS,
    [HIRE_TYPES.EXECUTIVE]: CANDIDATE_STAGES.OFFER,
};
export const CANDIDATE_STAGE_ORDER = [
    CANDIDATE_STAGES.APPLICATION,
    CANDIDATE_STAGES.INTERVIEW,
    CANDIDATE_STAGES.OFFER,
    CANDIDATE_STAGES.ONBOARDING_DOCS,
    CANDIDATE_STAGES.COMPLETE,
];
export const CANDIDATE_STAGE_LABELS = {
    [CANDIDATE_STAGES.APPLICATION]: 'Application',
    [CANDIDATE_STAGES.INTERVIEW]: 'Interview',
    [CANDIDATE_STAGES.OFFER]: 'Offer',
    [CANDIDATE_STAGES.AGREEMENT_SIGNING]: 'Agreement Signing',
    [CANDIDATE_STAGES.BACKGROUND_CHECK]: 'Background Check',
    [CANDIDATE_STAGES.ONBOARDING_DOCS]: 'Onboarding Docs',
    [CANDIDATE_STAGES.COMPLETE]: 'Complete',
    [CANDIDATE_STAGES.REJECTED]: 'Rejected',
    [CANDIDATE_STAGES.WITHDRAWN]: 'Withdrawn',
};
export const TERMINAL_STAGES = [
    CANDIDATE_STAGES.COMPLETE,
    CANDIDATE_STAGES.REJECTED,
    CANDIDATE_STAGES.WITHDRAWN,
];
export const OPEN_ROLE_STATUSES = {
    OPEN: 'open',
    PAUSED: 'paused',
    FILLED: 'filled',
    CLOSED: 'closed',
};
export const INTERVIEW_STATUSES = {
    SCHEDULED: 'scheduled',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    NO_SHOW: 'no_show',
};
export const AGREEMENT_TYPES = {
    EMPLOYMENT: 'employment',
    NDA: 'nda',
    NON_COMPETE: 'non_compete',
    CONTRACTOR: 'contractor',
    OTHER: 'other',
};
export const AGREEMENT_STATUSES = {
    PENDING: 'pending',
    SENT: 'sent',
    SIGNED: 'signed',
    REJECTED: 'rejected',
};
export const BG_CHECK_STATUSES = {
    PENDING: 'pending',
    INITIATED: 'initiated',
    IN_PROGRESS: 'in_progress',
    PASSED: 'passed',
    FAILED: 'failed',
    CANCELLED: 'cancelled',
};
export const AGREEMENT_TYPE_LABELS = {
    [AGREEMENT_TYPES.EMPLOYMENT]: 'Employment',
    [AGREEMENT_TYPES.NDA]: 'NDA',
    [AGREEMENT_TYPES.NON_COMPETE]: 'Non-Compete',
    [AGREEMENT_TYPES.CONTRACTOR]: 'Contractor',
    [AGREEMENT_TYPES.OTHER]: 'Other',
};
export const AGREEMENT_STATUS_LABELS = {
    [AGREEMENT_STATUSES.PENDING]: 'Pending',
    [AGREEMENT_STATUSES.SENT]: 'Sent',
    [AGREEMENT_STATUSES.SIGNED]: 'Signed',
    [AGREEMENT_STATUSES.REJECTED]: 'Rejected',
};
export const BG_CHECK_STATUS_LABELS = {
    [BG_CHECK_STATUSES.PENDING]: 'Pending',
    [BG_CHECK_STATUSES.INITIATED]: 'Initiated',
    [BG_CHECK_STATUSES.IN_PROGRESS]: 'In Progress',
    [BG_CHECK_STATUSES.PASSED]: 'Passed',
    [BG_CHECK_STATUSES.FAILED]: 'Failed',
    [BG_CHECK_STATUSES.CANCELLED]: 'Cancelled',
};
export const CANDIDATE_DOC_TYPES = {
    RESUME: 'resume',
    COVER_LETTER: 'cover_letter',
    INTERVIEW_NOTES: 'interview_notes',
    AGREEMENT: 'agreement',
    BACKGROUND_CHECK: 'background_check',
    ONBOARDING_DOC: 'onboarding_doc',
    SIGNED_AGREEMENT: 'signed_agreement',
    OTHER: 'other',
};
export const CANDIDATE_DOC_TYPE_LABELS = {
    [CANDIDATE_DOC_TYPES.RESUME]: 'Resume',
    [CANDIDATE_DOC_TYPES.COVER_LETTER]: 'Cover Letter',
    [CANDIDATE_DOC_TYPES.INTERVIEW_NOTES]: 'Interview Notes',
    [CANDIDATE_DOC_TYPES.AGREEMENT]: 'Agreement',
    [CANDIDATE_DOC_TYPES.BACKGROUND_CHECK]: 'Background Check',
    [CANDIDATE_DOC_TYPES.ONBOARDING_DOC]: 'Onboarding Doc',
    [CANDIDATE_DOC_TYPES.SIGNED_AGREEMENT]: 'Signed Agreement',
    [CANDIDATE_DOC_TYPES.OTHER]: 'Other',
};
export const NOTIFICATION_TYPES_HIRING = {
    AGREEMENTS_NEEDED: 'agreements_needed',
    BG_CHECK_NEEDED: 'bg_check_needed',
    ONBOARDING_DOCS_NEEDED: 'onboarding_docs_needed',
    INTERVIEW_SCHEDULED: 'interview_scheduled',
    PIPELINE_COMPLETE: 'pipeline_complete',
};
// ============================================================================
// API Constants
// ============================================================================
export const API_ERROR_CODES = {
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    DOMAIN_NOT_ALLOWED: 'DOMAIN_NOT_ALLOWED',
    INVITATION_REQUIRED: 'INVITATION_REQUIRED',
    INVITATION_EXPIRED: 'INVITATION_EXPIRED',
    INVITATION_ALREADY_USED: 'INVITATION_ALREADY_USED',
    EMAIL_ALREADY_REGISTERED: 'EMAIL_ALREADY_REGISTERED',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    TOKEN_INVALID: 'TOKEN_INVALID',
    REFRESH_TOKEN_INVALID: 'REFRESH_TOKEN_INVALID',
    INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
    GOOGLE_AUTH_FAILED: 'GOOGLE_AUTH_FAILED',
    ACCOUNT_INACTIVE: 'ACCOUNT_INACTIVE',
};
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;
// ============================================================================
// Validation Constants
// ============================================================================
export const VALIDATION_RULES = {
    EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    MIN_PASSWORD_LENGTH: 8,
    MAX_NAME_LENGTH: 100,
    MAX_DESCRIPTION_LENGTH: 1000,
};
//# sourceMappingURL=index.js.map